'use client';
import { Container } from '@/components/layout/container/container';
import { Badge } from '@/components/ui/badge/badge';
import RichTextViewer from '@/components/ui/rich-text-viewer/rich-text-viewer';
import { Banner } from '@/components/ui/banner/banner';
import { Image } from '@/components/ui/image/image';
import { Step } from '@/components/ui/step/step';
import { cn } from '@/lib/utils';
import type { SectionBgVariant } from '@/lib/types/misc';
import { buildFormContentFromLinkFields } from '@/lib/utils/form-data';
import { Entry } from 'contentful';
import type { Document } from '@contentful/rich-text-types';
import { motion } from 'motion/react';
import { HTMLAttributes } from 'react';
import { TypeBannerSkeleton } from 'types/contentful/TypeBanner';
import { TypeCardsSkeleton as TypeUniversalCardSkeleton } from 'types/contentful/TypeCards';
import { TypeSectionBackgroundSkeleton } from 'types/contentful/TypeSectionBackground';
import styles from './information-with-steps.module.scss';

export type InformationWithSteps = {
  id?: string;
  badge?: string;
  title?: string;
  description?: Document;
  steps?:
    | (Entry<TypeUniversalCardSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS', string> | undefined)[]
    | undefined;
  innerCta?: Entry<TypeBannerSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>['fields'] | undefined;
  background?:
    | Entry<TypeSectionBackgroundSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS', string>['fields']
    | undefined;
};

export const InformationWithSteps = ({
  id,
  badge,
  title,
  description,
  steps,
  innerCta,
  className,
  background,
  ...props
}: InformationWithSteps & HTMLAttributes<HTMLElement>) => {
  // Extract background properties
  const backgroundVariant = background?.variant || 'grey';
  const backgroundImage = background?.image?.fields?.file?.url;

  // Get step count for dynamic grid
  const stepCount = steps?.filter(Boolean).length || 0;
  const innerCtaFields = innerCta?.cta?.fields;
  const innerCtaId = innerCta?.cta?.fields.key;
  const innerCtaFormContent = buildFormContentFromLinkFields(innerCtaFields);

  return (
    <section
      id={id}
      className={cn(styles.component, styles[`component--${backgroundVariant}`], className)}
      {...props}
    >
      {backgroundVariant === 'image' && backgroundImage && (
        <div className={styles.component__background}>
          <Image
            src={backgroundImage}
            alt="Information with steps background"
            className={styles.component__background__image}
            fill={true}
          />
        </div>
      )}

      <Container className={styles.component_container}>
        <div className={styles.component_container_inner}>
          <div className={styles.component_container_inner_top}>
            {badge && <Badge text={badge} />}
            <div className={styles.component_heading}>
              <h2>{title}</h2>
              {description && <RichTextViewer content={description} />}
            </div>
          </div>

          <motion.div
            className={styles.component_steps}
            data-step-count={stepCount}
            initial={{ opacity: 1 }}
            animate={{ opacity: 1 }}
            transition={{ staggerChildren: 0.2 }}
          >
            {steps?.map((step, index) => {
              const item = step?.fields;
              if (!item) return null;
              return (
                <motion.div
                  key={item.title + index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.2 }}
                  viewport={{ once: true, amount: 0.3 }}
                >
                  <Step
                    index={index}
                    heading={item.title}
                    description={item.description}
                    backgroundVariant={backgroundVariant}
                  />
                </motion.div>
              );
            })}
          </motion.div>
        </div>

        {innerCta && (
          <Banner
            heading={innerCta.heading}
            subHeading={innerCta.subheading}
            cta={
              innerCtaFields
                ? {
                    label: innerCtaFields.label,
                    url: innerCtaFields.url,
                    type: innerCtaFields.type as 'Button' | 'Link',
                    icon: innerCtaFields.icon?.fields?.file?.url
                      ? {
                          src: innerCtaFields.icon.fields.file.url.startsWith('//')
                            ? `https:${innerCtaFields.icon.fields.file.url}`
                            : innerCtaFields.icon.fields.file.url,
                          alt:
                            innerCtaFields.icon.fields.description ||
                            innerCtaFields.icon.fields.title ||
                            undefined,
                        }
                      : undefined,
                    withArrow: true,
                    newWindow: innerCtaFields.newWindow ?? false,
                    destination: innerCtaFields.destination,
                    ctaId: innerCtaId,
                    ...innerCtaFormContent,
                  }
                : undefined
            }
            backgroundVariant={innerCta.background?.fields.variant as SectionBgVariant | undefined}
            backgroundImage={innerCta.background?.fields.image?.fields.file?.url}
            isDarkBackground={innerCta.background?.fields.isTheBackgroundDark}
          />
        )}
      </Container>
    </section>
  );
};

InformationWithSteps.displayName = 'InformationWithSteps';
